/**
 * Search System - Production Ready
 * Handles search functionality with clean, maintainable code
 * ~200 lines instead of 700+ lines
 */

(function() {
    'use strict';

    // Configuration
    const CONFIG = {
      BREAKPOINTS: {
        MOBILE: 767,
        DESKTOP: 1025
      },
      SELECTORS: {
        FALLBACK: '#search-fallback',
        DESKTOP_SEARCH: '#desktop-search',
        MOBILE_SEARCH: 'mobile-search.mobile-search, .mobile-search',
        MOBILE_INPUT: 'input, [role="searchbox"], [type="search"]',
        DESKTOP_CLEAR: '.desktop_clear_search',
        MOBILE_CLEAR: '.mobile_clear_search',
        HEADER: '#shopify-section-header',
        SITE_HEADER: '.site-header',
        ANNOUNCEMENT: '.announcement-bar',
        MAIN_CONTENT: '#MainContent'
      },
      SCROLL: {
        HIDE_AT: 120,
        SHOW_AT: 80,
        TOP_Y: 2
      }
    };

    // State
    let isInitialized = false;
    let $ = null;
    let mediaQuery = null;
    let scrollLocked = false;
    let savedScrollY = 0;

    // PERF: Cache for dimension values to reduce forced reflows
    let cachedDimensions = {
      siteHeight: 64,
      announcementHeight: 0,
      lastUpdate: 0
    };

    // Utility functions
    const isMobile = () => mediaQuery ? !mediaQuery.matches : window.innerWidth < CONFIG.BREAKPOINTS.DESKTOP;

    // PERF: Batch DOM writes using requestAnimationFrame to avoid forced reflows
    const batchDOMWrite = (fn) => {
      if (window.requestAnimationFrame) {
        requestAnimationFrame(fn);
      } else {
        fn();
      }
    };

    // PERF: Update cached dimensions with debouncing to reduce layout thrashing
    const updateCachedDimensions = () => {
      const now = Date.now();
      // Only update if more than 100ms since last update
      if (now - cachedDimensions.lastUpdate < 100) return;

      const siteHeader = document.querySelector(CONFIG.SELECTORS.SITE_HEADER);
      const announcement = document.querySelector(CONFIG.SELECTORS.ANNOUNCEMENT);

      // Batch all reads together
      cachedDimensions.siteHeight = siteHeader ? Math.round(siteHeader.offsetHeight) : 64;
      cachedDimensions.announcementHeight = announcement ? Math.round(announcement.offsetHeight) : 0;
      cachedDimensions.lastUpdate = now;
    };
    const debounce = (func, wait) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        // Use requestIdleCallback for better performance when available
        if (window.requestIdleCallback && wait > 16) {
          timeout = setTimeout(() => {
            requestIdleCallback(() => func.apply(this, args), { timeout: wait });
          }, 0);
        } else {
          timeout = setTimeout(() => func.apply(this, args), wait);
        }
      };
    };

    // Initialize when jQuery is ready
    function init() {
      if (typeof jQuery === 'undefined') {
        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
          setTimeout(init, 50);
        });
        return;
      }

      if (isInitialized) return;
      isInitialized = true;
      $ = jQuery;

      setupMediaQuery();
      initGlobalSearch();
      initMobileSearch();
      initScrollLock();
    }

    function setupMediaQuery() {
      mediaQuery = window.matchMedia(`(min-width: ${CONFIG.BREAKPOINTS.DESKTOP}px)`);
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleMediaChange);
      }
    }

    function handleMediaChange() {
      if (!isMobile()) {
        unlockScroll();
        resetHeaderVars();
      }
    }

    // Global Search (Desktop + Mobile)
    function initGlobalSearch() {
      const $fallback = $(CONFIG.SELECTORS.FALLBACK);
      if (!$fallback.length) return;

      // Focus handler - using direct binding to catch native focus events
      $(`${CONFIG.SELECTORS.DESKTOP_SEARCH}, ${CONFIG.SELECTORS.MOBILE_SEARCH} ${CONFIG.SELECTORS.MOBILE_INPUT}`).on('focus', function() {
        const query = $(this).val().trim();
        if (query.length === 0) {
          $fallback.removeClass('hidden').attr('aria-hidden', 'false');
        }
      });

      // Clear handlers
      $(document).on('click', `${CONFIG.SELECTORS.DESKTOP_CLEAR}, ${CONFIG.SELECTORS.MOBILE_CLEAR}`, function(e) {
        e.preventDefault();
        const $inputs = $(`${CONFIG.SELECTORS.DESKTOP_SEARCH}, ${CONFIG.SELECTORS.MOBILE_SEARCH} ${CONFIG.SELECTORS.MOBILE_INPUT}`);
        $inputs.val('');
        if (document.activeElement) document.activeElement.blur();
        $fallback.addClass('hidden').attr('aria-hidden', 'true');
        $(window).trigger('search:close');
      });

      // Keyboard support
      $(document).on('keydown', `${CONFIG.SELECTORS.DESKTOP_SEARCH}, ${CONFIG.SELECTORS.MOBILE_SEARCH} ${CONFIG.SELECTORS.MOBILE_INPUT}`, function(e) {
        if (e.key === 'Escape') {
          if (document.activeElement) document.activeElement.blur();
          $fallback.addClass('hidden').attr('aria-hidden', 'true');
        }
      });
    }

    // Mobile Search (Scroll management)
    function initMobileSearch() {
      const $header = $(CONFIG.SELECTORS.HEADER);
      if (!$header.length || !isMobile()) return;

      const $siteHeader = $header.find(CONFIG.SELECTORS.SITE_HEADER);
      const $announcement = $header.find(CONFIG.SELECTORS.ANNOUNCEMENT);
      const $main = $(CONFIG.SELECTORS.MAIN_CONTENT);
      const $mobileInput = $(CONFIG.SELECTORS.MOBILE_SEARCH).find(CONFIG.SELECTORS.MOBILE_INPUT).first();

      let collapsed = false;
      let locked = false;
      let savedScrollY = null;
      let movedWhileLocked = false;

      const getScrollY = () => window.pageYOffset || document.documentElement.scrollTop || 0;

      // PERF: Use cached dimensions to avoid forced reflows
      const getSiteHeight = () => cachedDimensions.siteHeight;
      const getAnnouncementHeight = () => cachedDimensions.announcementHeight;

      // PERF: Batch DOM writes using requestAnimationFrame
      function setHeaderVars(includeAnnouncement = true) {
        batchDOMWrite(() => {
          if (!isMobile()) {
            document.documentElement.style.setProperty('--header-offset', '0px');
            return;
          }

          // Use cached values to avoid forced reflows
          const siteH = getSiteHeight();
          const annH = getAnnouncementHeight();
          document.documentElement.style.setProperty('--hdr-height', `${siteH}px`);
          document.documentElement.style.setProperty('--ann-h', `${annH}px`);

          const padding = includeAnnouncement ? siteH + annH : siteH;
          if ($main.length) {
            $main[0].style.setProperty('--header-offset', `${padding}px`);
          }
        });
      }

      // PERF: Update dimensions cache on resize and orientation change
      const debouncedDimensionUpdate = debounce(updateCachedDimensions, 100);
      window.addEventListener('resize', debouncedDimensionUpdate, { passive: true });
      window.addEventListener('orientationchange', debouncedDimensionUpdate, { passive: true });

      // Initial dimension cache
      updateCachedDimensions();

      function collapse() {
        if (!isMobile()) return;
        collapsed = true;
        $header.addClass('ann--collapsed');
        setHeaderVars(false);
      }

      function expand() {
        if (!isMobile()) return;
        collapsed = false;
        $header.removeClass('ann--collapsed');
        setHeaderVars(getScrollY() <= CONFIG.SCROLL.TOP_Y);
      }

      function lockAnnouncement() {
        if (!isMobile() || locked) return;
        locked = true;
        $header.addClass('ann--locked');
        if (!collapsed) collapse();
      }

      function unlockAnnouncement() {
        if (!isMobile() || !locked) return;
        locked = false;
        $header.removeClass('ann--locked');
        const y = getScrollY();
        if (y < CONFIG.SCROLL.SHOW_AT && y <= CONFIG.SCROLL.TOP_Y) {
          expand();
        } else {
          collapse();
        }
      }

      // Scroll handler - optimized for mobile performance
      const onScroll = debounce(() => {
        if (!isMobile()) return;
        if (locked) {
          movedWhileLocked = true;
          return;
        }

        const y = getScrollY();
        if (!collapsed && y > CONFIG.SCROLL.HIDE_AT) {
          collapse();
        } else if (collapsed && y < CONFIG.SCROLL.SHOW_AT) {
          expand();
        }

        if (!collapsed && y <= CONFIG.SCROLL.TOP_Y) {
          setHeaderVars(true);
        }
      }, 100); // Increased from 16ms to 100ms for better mobile performance

      // Search handlers
      function onSearchOpen() {
        if (!isMobile()) return;
        if (savedScrollY === null) savedScrollY = getScrollY();
        movedWhileLocked = false;
        lockAnnouncement();
      }

      function onSearchClose() {
        if (!isMobile()) return;
        unlockAnnouncement();
        if (savedScrollY !== null && !movedWhileLocked) {
          window.scrollTo({ top: savedScrollY, behavior: 'auto' });
        }
        savedScrollY = null;
        movedWhileLocked = false;
      }

      // Event listeners - consolidated to prevent conflicts
      $(window).on('scroll', onScroll);
      $(window).on('resize', debounce(() => {
        setHeaderVars(isMobile() && !$header.hasClass('ann--collapsed') && getScrollY() <= CONFIG.SCROLL.TOP_Y);
        // Also handle scroll lock resize
        if (!isMobile()) unlockScroll();
        else updateScrollLock();
      }, 250));

      if ($mobileInput.length) {
        $mobileInput.on('focus', onSearchOpen);
        $(CONFIG.SELECTORS.MOBILE_SEARCH).on('focusin', onSearchOpen);
      }

      $(window).on('search:close', onSearchClose);

      // Initialize
      setHeaderVars(isMobile() && getScrollY() <= CONFIG.SCROLL.TOP_Y);
    }

    // Scroll Lock (Mobile only)
    function initScrollLock() {
      const $html = document.documentElement;
      const $body = document.body;

      function isSearchOpen() {
        const fallback = document.getElementById('search-fallback');
        const predictive = document.querySelector('[js-predictive-search]');
        return (fallback && !fallback.classList.contains('hidden')) ||
               (predictive && getComputedStyle(predictive).display !== 'none');
      }

      function lockScroll() {
        if (!isMobile() || scrollLocked) return;
        scrollLocked = true;
        savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;

        $html.classList.add('scroll-locked', 'search-overlay-open');
        $body.classList.add('scroll-locked', 'search-overlay-open');
        $body.style.position = 'fixed';
        $body.style.top = `-${savedScrollY}px`;
        $body.style.left = '0';
        $body.style.right = '0';
        $body.style.width = '100%';
        $body.style.overscrollBehavior = 'none';
      }

      function unlockScroll() {
        if (!scrollLocked) return;
        scrollLocked = false;

        $html.classList.remove('scroll-locked', 'search-overlay-open');
        $body.classList.remove('scroll-locked', 'search-overlay-open');
        $body.style.position = '';
        $body.style.top = '';
        $body.style.left = '';
        $body.style.right = '';
        $body.style.width = '';
        $body.style.overscrollBehavior = '';

        window.scrollTo(0, savedScrollY);
      }

      function updateScrollLock() {
        if (isMobile() && isSearchOpen()) {
          lockScroll();
        } else {
          unlockScroll();
        }
      }

      // Watch for search state changes
      const observer = new MutationObserver(updateScrollLock);
      const fallback = document.getElementById('search-fallback');
      const predictive = document.querySelector('[js-predictive-search]');

      if (fallback) observer.observe(fallback, { attributes: true, attributeFilter: ['class', 'hidden'] });
      if (predictive) observer.observe(predictive, { attributes: true, attributeFilter: ['class', 'style'] });

      // Click handlers
      document.addEventListener('click', (e) => {
        if (e.target.closest('[js-open-search]') || e.target.closest('[js-close-search]')) {
          // Use requestAnimationFrame for better performance than setTimeout(0)
          requestAnimationFrame(updateScrollLock);
        }
      }, { passive: true });

      // Resize handler - consolidated with existing resize handler
      // Moved to prevent duplicate event listeners

      // Initialize
      updateScrollLock();
    }

    function resetHeaderVars() {
      document.documentElement.style.setProperty('--header-offset', '0px');
    }

    // Cleanup
    function cleanup() {
      if (mediaQuery && mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleMediaChange);
      }
      isInitialized = false;
    }

    // Initialize
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    // Export
    window.SearchSystem = { cleanup };

    // Cleanup on page hide (bfcache-friendly)
    window.addEventListener('pagehide', cleanup);
  })();
